import { useId, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/utils/cn';

interface TextFieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'id'> {
  label: string;
  /** Icône affichée dans la gouttière gauche. */
  icon?: ReactNode;
  /** Élément interactif à droite (bascule d'affichage du mot de passe…). */
  trailing?: ReactNode;
  /** Message d'erreur. Sa présence bascule le champ en état invalide. */
  error?: string | undefined;
  /** Texte d'aide, masqué lorsqu'une erreur est affichée. */
  hint?: string;
  /** `react-hook-form` transmet sa `ref` via `register()`. */
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Champ de saisie avec libellé, icône et gestion d'erreur.
 *
 * Les contours bleu profond reprennent la palette du logo, avec des angles
 * légèrement arrondis. Le violet est réservé au focus, le rouge aux erreurs.
 *
 * Points d'accessibilité, souvent oubliés sur ce type de composant :
 *  - `useId` relie le `<label>` à l'`<input>` sans identifiant codé en dur,
 *    ce qui reste correct même avec plusieurs instances sur la page ;
 *  - `aria-invalid` annonce l'état d'erreur aux lecteurs d'écran ;
 *  - `aria-describedby` pointe vers le message, qui est donc lu après le
 *    libellé — sans cela l'erreur est visible mais inaudible ;
 *  - `role="alert"` fait annoncer le message dès son apparition.
 */
export const TextField = ({
  label,
  icon,
  trailing,
  error,
  hint,
  className,
  ref,
  ...rest
}: TextFieldProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const hasError = Boolean(error);

  return (
    <div className="space-y-[clamp(0.25rem,0.9vh,0.5rem)]">
      <label htmlFor={id} className="text-navy-900 block text-sm font-semibold">
        {label}
      </label>

      <div
        className={cn(
          'group relative flex items-center rounded-lg border bg-navy-50/60 bg-linear-to-b from-white/80 to-transparent',
          // Transition ciblée plutôt que `transition-all` : n'anime que ce qui
          // change réellement, et n'introduit aucune animation de géométrie.
          'transition-[border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none',
          hasError
            ? [
                'border-red-300',
                !rest.disabled && 'hover:border-red-400',
                'focus-within:border-red-500 focus-within:ring-[3px] focus-within:ring-red-500/12',
              ]
            : [
                'border-navy-200/80',
                !rest.disabled && 'hover:border-navy-300',
                // Le focus est une interaction : c'est le seul moment où le
                // violet apparaît sur un champ.
                'focus-within:border-accent-700 focus-within:ring-accent-700/12 focus-within:ring-[3px]',
              ],
          // Même reflet supérieur que le bouton, adapté à une surface claire.
          'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(15_42_95/0.03),0_2px_4px_-1px_rgb(15_42_95/0.08)]',
          !rest.disabled &&
            'hover:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(15_42_95/0.03),0_3px_6px_-2px_rgb(15_42_95/0.12)]',
          rest.disabled && 'bg-navy-50/50 bg-none opacity-60 shadow-none',
        )}
      >
        {icon ? (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none ml-2 grid size-8 shrink-0 place-items-center rounded-md text-[17px]',
              'transition-colors duration-200 motion-reduce:transition-none',
              hasError
                ? 'bg-red-50 text-red-600'
                : 'bg-navy-50 text-navy-600 group-focus-within:bg-accent-50 group-focus-within:text-accent-700',
            )}
          >
            {icon}
          </span>
        ) : null}

        <input
          id={id}
          ref={ref}
          aria-invalid={hasError}
          aria-describedby={(error ?? hint) ? messageId : undefined}
          className={cn(
            // Hauteur fluide : 50 px sur un écran standard, jusqu'à 44 px sur
            // un écran court, sans jamais descendre sous la cible tactile de 44 px.
            'h-[clamp(2.75rem,5.4vh,3.125rem)] min-w-0 w-full rounded-lg bg-transparent text-[15px] outline-none disabled:cursor-not-allowed',
            // La valeur saisie se détache du libellé et du texte d'accompagnement :
            // c'est la seule chose que l'utilisateur relit pour se vérifier.
            'text-navy-900',
            'placeholder:text-ink-500',
            // Détails qui font la différence entre « champ par défaut » et
            // « champ dessiné » : curseur et sélection aux couleurs de marque.
            'caret-accent-700 selection:bg-accent-100 selection:text-accent-900',
            icon ? 'pr-4 pl-3' : 'px-4',
            trailing && 'pr-2',
            className,
          )}
          {...rest}
        />

        {trailing ? <span className="flex shrink-0 items-center pr-2.5">{trailing}</span> : null}
      </div>

      {error ? (
        <p id={messageId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-ink-400 text-sm">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
